/**
 * Stripe's special cases: charge amounts carry two decimals in these
 * currencies regardless of what ISO or CLDR says, kept for backwards
 * compatibility on Stripe's side. Intl reports zero for ISK and UGX, and
 * dividing by one would render such a dispute at a hundred times its size.
 * https://docs.stripe.com/currencies#special-cases
 */
const STRIPE_AMOUNT_EXPONENT_OVERRIDES: Record<string, number> = {
  HUF: 2,
  ISK: 2,
  TWD: 2,
  UGX: 2,
};

/**
 * How many decimal places a provider amount in this currency carries.
 *
 * Stripe's own exceptions first, then Intl rather than a table here: JPY and
 * KRW have none, most currencies have two, and a few have three. Stripe sends
 * amounts in the currency's smallest unit, so 500 is five dollars but five
 * hundred yen — and a hard-coded divide by a hundred understates a yen
 * chargeback a hundredfold.
 */
function readMinorUnitExponent(currency: string): number {
  const override = STRIPE_AMOUNT_EXPONENT_OVERRIDES[currency];
  if (override !== undefined) {
    return override;
  }
  try {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });
    return formatter.resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    // An unknown code: two places is what almost every currency uses, and
    // guessing zero would overstate the amount by a hundred.
    return 2;
  }
}

/** Converts a provider minor-unit amount into its major-unit value. */
export function minorUnitsToAmount(amountMinor: number, currency: string): number {
  return amountMinor / 10 ** readMinorUnitExponent(currency.toUpperCase());
}

const currencyFormatters = new Map<string, Intl.NumberFormat>();

function getCurrencyFormatter(currency: string): Intl.NumberFormat | undefined {
  const cached = currencyFormatters.get(currency);
  if (cached) {
    return cached;
  }

  try {
    const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
    currencyFormatters.set(currency, formatter);
    return formatter;
  } catch {
    return undefined;
  }
}

/** Renders an amount already expressed in a currency's major unit. */
export function formatCurrencyAmount(amount: number, currency: string): string {
  const normalizedCurrency = currency.toUpperCase();
  return getCurrencyFormatter(normalizedCurrency)?.format(amount) ?? `${amount.toFixed(2)} ${normalizedCurrency}`;
}

/**
 * Renders a provider minor-unit amount for a person to read.
 *
 * One implementation, because the customer's billing page and the operator's
 * dispute queue show the same chargeback and must not disagree about what it
 * was worth.
 */
export function formatMinorAmount(amountMinor: number, currency: string): string {
  const normalizedCurrency = currency.toUpperCase();
  const amount = minorUnitsToAmount(amountMinor, normalizedCurrency);
  return getCurrencyFormatter(normalizedCurrency)?.format(amount) ?? `${amount} ${normalizedCurrency}`;
}
